// Consumer-side mirror of the admin editor's "Storefront Styles" feature — named, reusable element
// style presets (Squarespace-style). The composed, read-only blob arrives as a single JSON string on
// IStorefrontSettings.stylesConfig:
//
//   { "styles": [ { id, name, elementType, scope, sortOrder, parts: { <part>: { camelCase CSS } } } ] }
//
// An element's local style dict (a page-config key such as `addToCartButton`, or a section's
// styleProps dictionary) may carry the reserved key `$styleRef: "<styleId>"`. Resolution is
// preset-under-local: the referenced style's relevant PART is spread first and the local dict on
// top, so a hand-tweaked property always beats the preset. `$styleRef` is ALWAYS stripped from the
// output — it must never reach a React `style` prop or a CSS declaration.
//
// The same blob also carries the storefront's `palette` and `fontPack` docs (one active doc each),
// whose slots any style VALUE can reference through a `$palette:`/`$font:` token. See the "Palette +
// font pack tokens" section below; token resolution runs as the LAST step of every resolver here.
//
// IMPORTANT: strictly additive and defensive. Without `stylesConfig`, and for any dict that carries
// neither a `$styleRef` nor a token, every function here returns the caller's own object BY IDENTITY
// — so a storefront with no styles behaves exactly as it does today (no new objects, no changed
// useMemo identities).

// The reserved key an element dict uses to point at a named style.
export const STYLE_REF_KEY = '$styleRef';

export type StorefrontStyleElementType = 'button' | 'card' | 'section' | 'typography' | 'form' | 'palette' | 'fontPack';

// Conventional part names per element type (renderer-defined; unknown parts are simply ignored):
//   button     → base | hover | label
//   card       → card | title | price | image | hover
//   section    → band | title | header | footer | product
//   typography → heading | body
//   form       → input | label | focus
export type StylePart =
    | 'base' | 'hover' | 'label'
    | 'card' | 'title' | 'price' | 'image'
    | 'band' | 'header' | 'footer' | 'product'
    | 'heading' | 'body'
    | 'input' | 'focus'
    // The page-level layers a section can override — see SECTION_OVERRIDE_PARTS.
    | 'productCard' | 'productCardHover' | 'productTitle' | 'productPrice' | 'productImage'
    | 'addToCartButton' | 'scrollButton' | 'scrollbar' | 'moreLikeThisButton' | 'sectionTitleText';

/**
 * MIRROR of the admin's SECTION_OVERRIDE_PARTS (ac-main/web stylesConfig.ts). The `homePageConfig` keys a
 * SECTION may override, paired with the part of a `section` style that carries each one. Change them in
 * both places or in neither — the admin authors against this table and the shopper renders against it.
 *
 * Part names are prefixed where a bare one would collide with a part a section style already has: `title`
 * is the section's own title BAND, so a product's name is `productTitle`.
 */
export const SECTION_OVERRIDE_PARTS: Record<string, StylePart> = {
    card: 'productCard',
    cardHover: 'productCardHover',
    title: 'productTitle',
    price: 'productPrice',
    image: 'productImage',
    addToCartButton: 'addToCartButton',
    scrollButton: 'scrollButton',
    scrollbar: 'scrollbar',
    moreLikeThisButton: 'moreLikeThisButton',
    sectionTitleText: 'sectionTitleText',
};

// A camelCase CSS dictionary — the shape every element style takes (ISearchElementStyle and the
// section StyleProps are both compatible with it). Used as the default type parameter of the
// resolvers so a call that passes no local dict still returns something spreadable.
export type IStyleDict = { [prop: string]: any };

export interface IStorefrontNamedStyle {
    id: string;
    name?: string;
    elementType?: StorefrontStyleElementType | string;
    // 'storefront' (this storefront) or 'global' (org-wide). Display-only for the preset element
    // types; for `palette`/`fontPack` it also breaks the tie between two active docs (see
    // getActiveDoc): storefront-scoped beats global.
    scope?: string;
    sortOrder?: number;
    // The single active palette / font pack for the storefront. Only meaningful on the `palette` and
    // `fontPack` element types, where at most one doc per type is active at a time. Read strictly as
    // `=== true` so a stray truthy value in the blob can never activate a doc.
    isActive?: boolean;
    // part -> camelCase CSS dictionary. A `palette` doc instead carries { colors: {slot -> color} }
    // and a `fontPack` doc { fonts: { heading, body } }.
    parts?: Record<string, Record<string, unknown> | undefined>;
}

export interface IStylesConfig {
    styles?: IStorefrontNamedStyle[];
}

// Parsed form: the ordered list (panel order) plus an id lookup for ref resolution.
export interface IParsedStorefrontStyles {
    list: IStorefrontNamedStyle[];
    byId: Record<string, IStorefrontNamedStyle>;
}

export const EMPTY_STOREFRONT_STYLES: IParsedStorefrontStyles = { list: [], byId: {} };

// Safe parse: absence, malformed JSON, or an unexpected shape all yield the empty set, so an
// unconfigured storefront never throws and never resolves a ref.
//
// Entries are kept as-authored (no per-field rewriting), so every field the composed blob carries —
// including `isActive` on a palette/fontPack doc — survives onto IStorefrontNamedStyle. Validation of
// `isActive` happens at the point of use (getActiveDoc reads it strictly as `=== true`), which keeps
// a JSON value of `"true"` or `1` from silently activating a doc.
export function parseStylesConfig(raw?: string): IParsedStorefrontStyles {
    if (!raw) return EMPTY_STOREFRONT_STYLES;
    let parsed: IStylesConfig | undefined;
    try {
        parsed = JSON.parse(raw) as IStylesConfig;
    } catch {
        return EMPTY_STOREFRONT_STYLES;
    }
    const styles = parsed && Array.isArray(parsed.styles) ? parsed.styles : undefined;
    if (!styles || styles.length === 0) return EMPTY_STOREFRONT_STYLES;
    const list: IStorefrontNamedStyle[] = [];
    const byId: Record<string, IStorefrontNamedStyle> = {};
    for (const style of styles) {
        if (!style || typeof style !== 'object' || typeof style.id !== 'string' || !style.id) continue;
        list.push(style);
        byId[style.id] = style;
    }
    if (list.length === 0) return EMPTY_STOREFRONT_STYLES;
    list.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    return { list, byId };
}

// ---------------------------------------------------------------------------------------------
// Module-level registry
//
// The style builders (searchPageConfig's elementStyleFrom et al.) are called from ~180 sites across
// every page and component, almost none of which have the settings object in hand — several are
// plain module functions, not components. Threading settings through each would touch every call
// site and every intermediate prop chain. Instead the parsed styles live in a module-level registry
// that is set from the ONE place the settings blob enters the app: the getStorefront /
// getBasicStorefront `transformResponse` in Api.ts (plus an idempotent re-sync during Layout's
// render, which covers a warm RTK cache where transformResponse doesn't run again). Both run before
// any consumer renders with that data, so builders read a populated registry on first paint.
// Every resolver also accepts the parsed styles explicitly, which is what tests use.
// ---------------------------------------------------------------------------------------------

let registry: IParsedStorefrontStyles = EMPTY_STOREFRONT_STYLES;
let registryRaw: string | undefined;

// Idempotent: re-parsing is skipped when the raw blob is unchanged, so calling this on every render
// is free and never changes object identity.
export function setStorefrontStyles(raw?: string): void {
    if (raw === registryRaw) return;
    registryRaw = raw;
    registry = parseStylesConfig(raw);
}

export function getStorefrontStyles(): IParsedStorefrontStyles {
    return registry;
}

// ---------------------------------------------------------------------------------------------
// Palette + font pack tokens
//
// A `palette` doc (parts.colors) and a `fontPack` doc (parts.fonts) turn the storefront's brand into
// named slots. Any style VALUE anywhere styles flow may reference one:
//
//   "$palette:accent"          -> the active palette's colors.accent
//   "$palette:surface@0.55"    -> rgba(r,g,b,0.55) from colors.surface
//   "$font:heading"            -> the active font pack's fonts.heading
//
// A token that cannot be resolved (no active doc, unknown slot, unparseable hex, bad alpha) DROPS the
// property entirely, so the element falls back to whatever it renders without it. Anything that is
// not a token passes through untouched, and a dict with no tokens comes back BY IDENTITY.
//
// Self-contained on purpose: this whole section is mirrored byte-for-byte by the admin editor's
// preview, so it takes no dependency on the renderer's other helpers (hence the local hex parser).
// ---------------------------------------------------------------------------------------------

// The palette's colour slots, in emit order. Also the allow-list for token resolution and for the
// :root CSS variables, so an unexpected key in the blob can never reach a style attribute.
export const PALETTE_SLOTS = [
    'background', 'surface', 'text', 'heading', 'accent', 'accentText', 'menuBar', 'menuBarText', 'border',
] as const;
export type PaletteSlot = typeof PALETTE_SLOTS[number];

export const FONT_SLOTS = ['heading', 'body'] as const;
export type FontSlot = typeof FONT_SLOTS[number];

const PALETTE_TOKEN_PREFIX = '$palette:';
const FONT_TOKEN_PREFIX = '$font:';

// Alpha must be a plain unsigned decimal in [0, 1] — no sign, no exponent, no whitespace. Number()
// alone is far too permissive here ('' and ' ' both coerce to 0).
const ALPHA_PATTERN = /^\d*\.?\d+$/;

// #rgb / #rrggbb only. Anything else (named colour, rgb(), gradient, CSS var) is deliberately not
// parseable, which is what makes an @alpha token on it drop rather than emit nonsense.
function hexRgb(color: unknown): { r: number; g: number; b: number } | undefined {
    if (typeof color !== 'string') return undefined;
    const match = color.trim().match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
    if (!match) return undefined;
    const hex = match[1].length === 3
        ? `${match[1][0]}${match[1][0]}${match[1][1]}${match[1][1]}${match[1][2]}${match[1][2]}`
        : match[1];
    const value = parseInt(hex, 16);
    return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

// The active doc for an element type. At most one is active; when both a storefront-scoped and a
// global doc claim it, the storefront's own wins. Within a scope the first in panel order wins, so
// a malformed blob still resolves deterministically.
function getActiveDoc(styles: IParsedStorefrontStyles, elementType: string): IStorefrontNamedStyle | undefined {
    let global: IStorefrontNamedStyle | undefined;
    for (const style of styles.list) {
        if (style.elementType !== elementType || style.isActive !== true) continue;
        if (style.scope === 'storefront') return style;
        if (!global) global = style;
    }
    return global;
}

export function getActivePalette(styles: IParsedStorefrontStyles = getStorefrontStyles()): IStorefrontNamedStyle | undefined {
    return getActiveDoc(styles, 'palette');
}

export function getActiveFontPack(styles: IParsedStorefrontStyles = getStorefrontStyles()): IStorefrontNamedStyle | undefined {
    return getActiveDoc(styles, 'fontPack');
}

function dictOf(style: IStorefrontNamedStyle | undefined, key: string): Record<string, unknown> | undefined {
    const value = style?.parts?.[key];
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : undefined;
}

// The active palette's slot -> colour map, or undefined when no palette is active.
export function activePaletteColors(styles: IParsedStorefrontStyles = getStorefrontStyles()): Record<string, unknown> | undefined {
    return dictOf(getActivePalette(styles), 'colors');
}

// The active font pack's slot -> font-family map, or undefined when none is active.
export function activeFontPackFonts(styles: IParsedStorefrontStyles = getStorefrontStyles()): Record<string, unknown> | undefined {
    return dictOf(getActiveFontPack(styles), 'fonts');
}

// Cheap pre-check so the overwhelmingly common token-free value costs one typeof and one charCode.
function isStyleToken(value: unknown): value is string {
    return typeof value === 'string'
        && value.charCodeAt(0) === 36 /* $ */
        && (value.indexOf(PALETTE_TOKEN_PREFIX) === 0 || value.indexOf(FONT_TOKEN_PREFIX) === 0);
}

/**
 * Resolve a single `$palette:` / `$font:` token to its CSS value, or undefined when it cannot be
 * resolved (which means the property carrying it gets dropped). Exported for the admin mirror and
 * for call sites that resolve a lone value rather than a dictionary.
 */
export function resolveStyleToken(
    value: string,
    styles: IParsedStorefrontStyles = getStorefrontStyles(),
): string | undefined {
    if (value.indexOf(FONT_TOKEN_PREFIX) === 0) {
        const slot = value.slice(FONT_TOKEN_PREFIX.length);
        if ((FONT_SLOTS as readonly string[]).indexOf(slot) < 0) return undefined;
        const font = activeFontPackFonts(styles)?.[slot];
        return typeof font === 'string' && font ? font : undefined;
    }
    if (value.indexOf(PALETTE_TOKEN_PREFIX) !== 0) return undefined;
    const body = value.slice(PALETTE_TOKEN_PREFIX.length);
    const at = body.indexOf('@');
    const slot = at >= 0 ? body.slice(0, at) : body;
    if ((PALETTE_SLOTS as readonly string[]).indexOf(slot) < 0) return undefined;
    const color = activePaletteColors(styles)?.[slot];
    if (typeof color !== 'string' || !color) return undefined;
    if (at < 0) return color;
    const rawAlpha = body.slice(at + 1);
    if (!ALPHA_PATTERN.test(rawAlpha)) return undefined;
    const alpha = Number(rawAlpha);
    if (!isFinite(alpha) || alpha < 0 || alpha > 1) return undefined;
    const rgb = hexRgb(color);
    if (!rgb) return undefined;
    return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

/**
 * Resolve every `$palette:` / `$font:` token among a style dictionary's own string values.
 *
 *  - No token anywhere  → the SAME object, by identity (a legacy storefront allocates nothing and
 *                         every existing useMemo identity and snapshot stays exactly as it was).
 *  - Resolvable token   → replaced with the concrete colour / font family.
 *  - Unresolvable token → the property is DELETED, so the element falls back to its default.
 *
 * Only the dictionary's own top-level values are considered; style dicts are flat camelCase CSS.
 */
export function resolvePaletteTokens<T extends object = IStyleDict>(
    dict: T | undefined,
    styles: IParsedStorefrontStyles = getStorefrontStyles(),
): T | undefined {
    if (!dict) return dict;
    const source = dict as Record<string, unknown>;
    let out: Record<string, unknown> | undefined;
    for (const key of Object.keys(source)) {
        const value = source[key];
        if (!isStyleToken(value)) continue;
        if (!out) out = { ...source };
        const resolved = resolveStyleToken(value, styles);
        if (resolved === undefined) delete out[key];
        else out[key] = resolved;
    }
    return (out ?? dict) as T;
}

// The storefront settings fields a token may be authored into. Deliberately an explicit list: these
// are the colour/font-bearing fields, and nothing else on the settings object should ever be
// rewritten by token resolution.
export const TOKENIZED_SETTINGS_FIELDS = [
    'menuBarColor',
    'menuBarTextColor',
    'backgroundColor',
    'textColor',
    'sectionBackgroundColor',
    'sectionHeaderBackgroundColor',
    'sectionFooterBackgroundColor',
    'headerFont',
    'defaultFont',
] as const;

/**
 * Resolve tokens in the storefront settings' typed style fields. Applied once where the settings
 * enter the renderer (Api.ts `transformResponse`), which is BEFORE anything derives from them — most
 * importantly the background-luminance dark/light theme pick — so every downstream consumer only
 * ever sees concrete values.
 *
 * An unresolvable token leaves the field UNSET, exactly as if the merchant had never filled it in.
 * Settings with no tokens come back by identity, so a legacy storefront is untouched.
 */
export function resolveSettingsTokens<T extends object | undefined>(
    settings: T,
    styles: IParsedStorefrontStyles = getStorefrontStyles(),
): T {
    if (!settings) return settings;
    const source = settings as unknown as Record<string, unknown>;
    let out: Record<string, unknown> | undefined;
    for (const field of TOKENIZED_SETTINGS_FIELDS) {
        const value = source[field];
        if (!isStyleToken(value)) continue;
        if (!out) out = { ...source };
        const resolved = resolveStyleToken(value, styles);
        if (resolved === undefined) delete out[field];
        else out[field] = resolved;
    }
    return (out ?? settings) as T;
}

// camelCase slot -> kebab-case CSS custom property suffix (accentText -> accent-text).
const kebab = (name: string) => name.replace(/[A-Z]/g, (m) => `-${m.toLowerCase()}`);

// A value is only emitted into the <style> block when it can't terminate the declaration or the rule
// it sits in. Merchant-authored data goes straight into a stylesheet here, so this stays strict.
const isSafeCssValue = (value: unknown): value is string =>
    typeof value === 'string' && !!value.trim() && !/[;{}<>]/.test(value);

/**
 * The `:root` custom properties for the active palette + font pack, as a complete CSS rule ready to
 * drop into a <style> tag — `--sf-<slot>` for each populated colour slot, `--sf-<slot>-rgb` with its
 * `r,g,b` triple (hex slots only, for `rgb(var(--sf-accent-rgb) / 40%)` style composition), plus
 * `--sf-font-heading` / `--sf-font-body`.
 *
 * Returns undefined when neither a palette nor a font pack is active, so a storefront without them
 * emits no block at all.
 */
export function paletteCssVars(styles: IParsedStorefrontStyles = getStorefrontStyles()): string | undefined {
    const colors = activePaletteColors(styles);
    const fonts = activeFontPackFonts(styles);
    if (!colors && !fonts) return undefined;
    const declarations: string[] = [];
    if (colors) {
        for (const slot of PALETTE_SLOTS) {
            const value = colors[slot];
            if (!isSafeCssValue(value)) continue;
            const name = kebab(slot);
            declarations.push(`--sf-${name}: ${value.trim()};`);
            const rgb = hexRgb(value);
            if (rgb) declarations.push(`--sf-${name}-rgb: ${rgb.r},${rgb.g},${rgb.b};`);
        }
    }
    if (fonts) {
        for (const slot of FONT_SLOTS) {
            const value = fonts[slot];
            if (!isSafeCssValue(value)) continue;
            declarations.push(`--sf-font-${slot}: ${value.trim()};`);
        }
    }
    if (declarations.length === 0) return undefined;
    return `:root { ${declarations.join(' ')} }`;
}

// ---------------------------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------------------------

// The part a style falls back to when the requested part isn't authored on it — its element type's
// primary part. This makes a ref forgiving about the exact part a call site asks for (e.g. a section
// preset referenced from a generic container resolves to `band`). STATE parts are deliberately
// excluded from the fallback: falling back from `hover`/`focus` to the resting part would apply the
// whole preset only on hover, which is never what the merchant meant.
const PRIMARY_PART: Record<string, string> = {
    button: 'base',
    card: 'card',
    section: 'band',
    typography: 'body',
    form: 'input',
};
const STATE_PARTS: string[] = ['hover', 'focus'];

// Parts the primary-part fallback must NOT apply to. The state parts, for the reason above, plus every
// page-level override part (AC-1165) for a closely related one: a section preset that does not author,
// say, `productPrice` has to leave the page's price styling alone rather than fall back to `band` and
// paint the section container's background onto the price plate. It matters because a section pointed at
// a style gets a reference in EVERY one of these layers, so without this a style carrying only
// band/title/header/footer would splatter its band across the cards, the buttons and the scrollbar.
const NO_FALLBACK_PARTS: string[] = [...STATE_PARTS, ...Object.values(SECTION_OVERRIDE_PARTS)];

function readStyleRef(dict: unknown): string | undefined {
    if (!dict || typeof dict !== 'object') return undefined;
    const ref = (dict as Record<string, unknown>)[STYLE_REF_KEY];
    return typeof ref === 'string' && ref ? ref : undefined;
}

function hasStyleRefKey(dict: unknown): boolean {
    return !!dict && typeof dict === 'object' && STYLE_REF_KEY in (dict as Record<string, unknown>);
}

// Returns a copy of the dict without the reserved ref key. Only ever called when the key is present.
function stripStyleRef<T extends object>(dict: T): T {
    const copy: Record<string, unknown> = { ...(dict as Record<string, unknown>) };
    delete copy[STYLE_REF_KEY];
    return copy as T;
}

// The preset dictionary for `part` on `style`, or undefined when the style doesn't author it (and
// no primary-part fallback applies).
function partOf(style: IStorefrontNamedStyle | undefined, part: string): Record<string, unknown> | undefined {
    const parts = style?.parts;
    if (!parts || typeof parts !== 'object') return undefined;
    const exact = parts[part];
    if (exact && typeof exact === 'object') return exact;
    if (NO_FALLBACK_PARTS.indexOf(part) >= 0) return undefined;
    const primary = PRIMARY_PART[String(style?.elementType ?? '')];
    if (!primary || primary === part) return undefined;
    const fallback = parts[primary];
    return fallback && typeof fallback === 'object' ? fallback : undefined;
}

/**
 * Resolve one element style dictionary against the named styles.
 *
 *  - No dict            → undefined (callers' `if (!s) return {}` fast path is preserved).
 *  - No `$styleRef`     → the SAME object, by identity (zero behavior change, stable memo deps).
 *  - `$styleRef` set    → `{ ...preset.parts[part], ...local-without-$styleRef }` (local wins).
 *  - Dangling ref       → the local dict without `$styleRef`. Silent — no error, no logging.
 *
 * `$styleRef` is stripped from the result in every case, so it can never reach a style attribute.
 *
 * Palette/font tokens are resolved LAST, after the preset-under-local merge, so a token is honored
 * wherever it was authored: in the named style's part, in the element's own dict, or in both.
 */
export function resolveElementStyle<T extends object = IStyleDict>(
    local: T | undefined,
    part: StylePart | string,
    styles: IParsedStorefrontStyles = getStorefrontStyles(),
): T | undefined {
    if (!local) return local;
    if (!hasStyleRefKey(local)) return resolvePaletteTokens(local, styles);
    const stripped = stripStyleRef(local);
    const ref = readStyleRef(local);
    if (!ref) return resolvePaletteTokens(stripped, styles);
    const preset = partOf(styles.byId[ref], part);
    if (!preset) return resolvePaletteTokens(stripped, styles);
    return resolvePaletteTokens({ ...preset, ...(stripped as Record<string, unknown>) } as T, styles);
}

/**
 * Resolve the `hover` (or other state) part for the hover pipelines, which emit a scoped
 * `:hover` rule rather than an inline style.
 *
 * The ref may live on the hover dict itself, or — the common case — on the RESTING dict the hover
 * belongs to (`card` carries `$styleRef`, `cardHover` is the merchant's extra local hover tweak).
 * `refSource` supplies that second lookup. The preset's hover part goes UNDER the local hover dict.
 *
 * Returns undefined when there is neither a local hover dict nor a preset hover part, so the caller
 * emits no rule at all — exactly today's behavior.
 */
export function resolveHoverPart<T extends object = IStyleDict>(
    localHover: T | undefined,
    refSource?: object,
    styles: IParsedStorefrontStyles = getStorefrontStyles(),
    part: StylePart | string = 'hover',
): T | undefined {
    const ref = readStyleRef(localHover) ?? readStyleRef(refSource);
    const local = localHover && hasStyleRefKey(localHover) ? stripStyleRef(localHover) : localHover;
    if (!ref) return resolvePaletteTokens(local, styles);
    const preset = partOf(styles.byId[ref], part);
    if (!preset) return resolvePaletteTokens(local, styles);
    return resolvePaletteTokens({ ...preset, ...((local ?? {}) as Record<string, unknown>) } as T, styles);
}

/**
 * The `hover` (or other state) part of the named style a RESTING element references, for elements
 * that have no local hover dictionary of their own (buttons: there is no `addToCartButtonHover`
 * config key). Returns undefined when the element carries no ref or the preset authors no such part,
 * so the caller emits no rule at all.
 */
export function hoverPartFor(
    refSource?: object,
    styles: IParsedStorefrontStyles = getStorefrontStyles(),
    part: StylePart | string = 'hover',
): IStyleDict | undefined {
    const ref = readStyleRef(refSource);
    if (!ref) return undefined;
    const preset = partOf(styles.byId[ref], part);
    return preset ? resolvePaletteTokens({ ...preset } as IStyleDict, styles) : undefined;
}

/**
 * One page-level styling layer, as a specific SECTION sees it: the page's own value with that section's
 * override merged over it (AC-1165).
 *
 * `{ ...page, ...override }`. A section with no override for the key returns the page value UNTOUCHED —
 * the same object identity where nothing had to be merged — so every storefront that never sets one
 * renders byte-identically to before this existed, and the memoised style objects upstream keep their
 * referential equality.
 *
 * An override may itself carry a `$styleRef`, so the override side runs through normal element resolution
 * (named style's part under the override's own properties) before being merged over the page value.
 */
export function resolveSectionOverride<T extends object = IStyleDict>(
    pageValue: T | undefined,
    overrides: { [configKey: string]: object | undefined } | undefined,
    configKey: string,
    styles: IParsedStorefrontStyles = getStorefrontStyles(),
): T | undefined {
    const override = overrides?.[configKey];
    // Nothing to merge: hand back exactly what the page had, unchanged and identical.
    if (!override || Object.keys(override).length === 0) return pageValue;
    const resolved = resolveElementStyle(override as T, SECTION_OVERRIDE_PARTS[configKey] ?? configKey, styles);
    if (!resolved) return pageValue;
    return { ...(pageValue ?? {}), ...(resolved as Record<string, unknown>) } as T;
}

/**
 * Section StyleProps helper — the five `Dictionary<string, object>` style props on a section
 * (styleProps / titleStyleProps / headerStyleProps / footerStyleProps / productStyleProps) resolve
 * exactly like element styles, just with looser typing. Kept separate so the section call sites read
 * clearly and so `undefined` stays `undefined` (a spread of undefined is a no-op).
 */
export function resolveStyleProps<T extends object = IStyleDict>(
    props: T | undefined,
    part: StylePart | string,
    styles: IParsedStorefrontStyles = getStorefrontStyles(),
): T | undefined {
    return resolveElementStyle(props, part, styles);
}
